import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import { API } from "../api";
import Layout from "../components/Layout";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPercent,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";

const EMPTY_FORM = {
  cat_id: "",
  carmodel_id: "",
  season_id: "",
  spldisc_mindays: "",
  discount: "",
};

export default function SpecialDiscounter() {
  const navigate = useNavigate();

  // =====================================================
  // DATA
  // =====================================================

  const [groups, setGroups] = useState([]);
  const [carGroups, setCarGroups] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [durationSeasons, setDurationSeasons] =
    useState([]);

  const [filterCars, setFilterCars] = useState([]);
  const [formCars, setFormCars] = useState([]);

  // All models used by the table
  const [allCars, setAllCars] = useState([]);

  const [discounts, setDiscounts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [filterCarsLoading, setFilterCarsLoading] =
    useState(false);

  const [formCarsLoading, setFormCarsLoading] =
    useState(false);

  // =====================================================
  // FILTERS
  // =====================================================

  const [filterCatId, setFilterCatId] = useState("");
  const [filterCarModelId, setFilterCarModelId] =
    useState("");
  const [filterSeasonId, setFilterSeasonId] =
    useState("");

  // =====================================================
  // PAGINATION
  // =====================================================

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // =====================================================
  // FORM
  // =====================================================

  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // =====================================================
  // MODALS
  // =====================================================

  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);

  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // =====================================================
  // AUTH
  // =====================================================

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
    }
  }, [navigate]);

  // =====================================================
  // SAFE ARRAY DATA
  // =====================================================

  const getArrayData = useCallback((response) => {
    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.data?.data)) {
      return response.data.data;
    }

    return [];
  }, []);

  // =====================================================
  // MERGE ARRAY BY ID
  // =====================================================

  const mergeUniqueById = (
    previous,
    incoming,
    idField
  ) => {
    const map = new Map();

    [...previous, ...incoming].forEach(
      (item) => {
        if (
          item &&
          item[idField] !== undefined &&
          item[idField] !== null
        ) {
          map.set(
            String(item[idField]),
            item
          );
        }
      }
    );

    return Array.from(map.values());
  };

  // =====================================================
  // MERGE CARS
  // =====================================================

  const mergeCarsIntoAllCars = useCallback(
    (cars) => {
      if (!Array.isArray(cars)) {
        return;
      }

      if (cars.length === 0) {
        return;
      }

      setAllCars((previous) =>
        mergeUniqueById(
          previous,
          cars,
          "carmodel_id"
        )
      );
    },
    []
  );

  // =====================================================
  // LOAD META DATA
  // =====================================================

  const fetchMeta = useCallback(
    async () => {
      try {
        const [
          groupsResponse,
          seasonsResponse,
          carsResponse,
          carGroupsResponse,
          durationResponse,
        ] = await Promise.all([
          API.get(
            "/spacialdiscount/cat"
          ),

          API.get(
            "/spacialdiscount/seasons"
          ),

          API.get(
            "/spacialdiscount/models",
            {
              params: {
                selectedcarcategory: 0,
              },
            }
          ),

          API.get(
            "/cargroup",
            {
              params: {
                limit: 100,
              },
            }
          ),

          API.get(
            "/spacialdiscount/seasonSelect"
          ),
        ]);

        const groupData =
          getArrayData(
            groupsResponse
          );

        const seasonData =
          getArrayData(
            seasonsResponse
          );

        const carData =
          getArrayData(
            carsResponse
          );

        const carGroupData =
          getArrayData(
            carGroupsResponse
          );

        const durationData =
          getArrayData(
            durationResponse
          );

        setGroups(groupData);
        setSeasons(seasonData);

        setCarGroups(
          carGroupData
        );

        setDurationSeasons(
          durationData
        );

        setFilterCars(
          carData
        );

        setAllCars(
          carData
        );
      } catch (error) {
        console.error(
          "fetchMeta error:",
          error
        );

        toasts.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load dropdown data"
        );
      }
    },
    [getArrayData]
  );

  // =====================================================
  // INITIAL META
  // =====================================================

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  // =====================================================
  // LOAD FILTER MODELS
  // =====================================================

  const loadFilterCars = useCallback(
    async (catId = 0) => {
      setFilterCarsLoading(true);

      try {
        const response =
          await API.get(
            "/spacialdiscount/models",
            {
              params: {
                selectedcarcategory:
                  catId || 0,
              },
            }
          );

        const carData =
          getArrayData(response);

        setFilterCars(
          carData
        );

        mergeCarsIntoAllCars(
          carData
        );
      } catch (error) {
        console.error(
          "loadFilterCars error:",
          error
        );

        setFilterCars([]);

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load car models"
        );
      } finally {
        setFilterCarsLoading(
          false
        );
      }
    },
    [
      getArrayData,
      mergeCarsIntoAllCars,
    ]
  );

  // =====================================================
  // FILTER MODEL EFFECT
  // =====================================================

  useEffect(() => {
    loadFilterCars(
      filterCatId || 0
    );

    setFilterCarModelId("");
    setPage(1);
  }, [
    filterCatId,
    loadFilterCars,
  ]);

  // =====================================================
  // LOAD FORM MODELS
  // =====================================================

  const loadFormCars = useCallback(
    async (catId) => {
      if (!catId) {
        setFormCars([]);
        return [];
      }

      setFormCarsLoading(true);

      try {
        const response =
          await API.get(
            "/spacialdiscount/selectmodels",
            {
              params: {
                cat_id: catId,
              },
            }
          );

        const carData =
          getArrayData(response);

        setFormCars(
          carData
        );

        // IMPORTANT:
        // Store the models in allCars.
        mergeCarsIntoAllCars(
          carData
        );

        return carData;
      } catch (error) {
        console.error(
          "loadFormCars error:",
          error
        );

        setFormCars([]);

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load car models"
        );

        return [];
      } finally {
        setFormCarsLoading(
          false
        );
      }
    },
    [
      getArrayData,
      mergeCarsIntoAllCars,
    ]
  );

  // =====================================================
  // ENSURE TABLE LOOKUP DATA
  // =====================================================
  //
  // This is the IMPORTANT FIX.
  //
  // After fetching discounts, check every record and
  // make sure its group/model/season exists in local
  // React state.
  //
  // This makes the table independent of a browser reload.
  // =====================================================

  const ensureDiscountLookupData =
    useCallback(
      async (discountData) => {
        if (
          !Array.isArray(
            discountData
          ) ||
          discountData.length === 0
        ) {
          return;
        }

        try {
          // -------------------------------------------------
          // 1. Make sure groups exist
          // -------------------------------------------------

          const missingGroupIds =
            discountData
              .map(
                (item) =>
                  item.cat_id
              )
              .filter(
                (id, index, array) =>
                  id &&
                  array.findIndex(
                    (x) =>
                      String(x) ===
                      String(id)
                  ) === index &&
                  !groups.some(
                    (group) =>
                      String(
                        group.cat_id
                      ) ===
                      String(id)
                  )
              );

          if (
            missingGroupIds.length >
            0
          ) {
            const response =
              await API.get(
                "/spacialdiscount/cat"
              );

            const data =
              getArrayData(
                response
              );

            setGroups(
              (previous) =>
                mergeUniqueById(
                  previous,
                  data,
                  "cat_id"
                )
            );
          }

          // -------------------------------------------------
          // 2. Make sure seasons exist
          // -------------------------------------------------

          const missingSeasonIds =
            discountData
              .map(
                (item) =>
                  item.season_id
              )
              .filter(
                (id, index, array) =>
                  id &&
                  array.findIndex(
                    (x) =>
                      String(x) ===
                      String(id)
                  ) === index &&
                  !seasons.some(
                    (season) =>
                      String(
                        season.season_id
                      ) ===
                      String(id)
                  )
              );

          if (
            missingSeasonIds.length >
            0
          ) {
            const response =
              await API.get(
                "/spacialdiscount/seasons"
              );

            const data =
              getArrayData(
                response
              );

            setSeasons(
              (previous) =>
                mergeUniqueById(
                  previous,
                  data,
                  "season_id"
                )
            );
          }

          // -------------------------------------------------
          // 3. Make sure car models exist
          // -------------------------------------------------

          const missingModelRecords =
            discountData.filter(
              (discount) =>
                discount.carmodel_id &&
                !allCars.some(
                  (car) =>
                    String(
                      car.carmodel_id
                    ) ===
                    String(
                      discount.carmodel_id
                    )
                )
            );

          // Group missing models by car group.
          const categoryIds = [
            ...new Set(
              missingModelRecords
                .map(
                  (item) =>
                    item.cat_id
                )
                .filter(Boolean)
            ),
          ];

          for (
            const catId of categoryIds
          ) {
            try {
              const response =
                await API.get(
                  "/spacialdiscount/selectmodels",
                  {
                    params: {
                      cat_id: catId,
                    },
                  }
                );

              const carData =
                getArrayData(
                  response
                );

              mergeCarsIntoAllCars(
                carData
              );
            } catch (error) {
              console.error(
                "Failed to refresh models for category:",
                catId,
                error
              );
            }
          }
        } catch (error) {
          console.error(
            "ensureDiscountLookupData error:",
            error
          );
        }
      },
      [
        groups,
        seasons,
        allCars,
        getArrayData,
        mergeCarsIntoAllCars,
      ]
    );

  // =====================================================
  // FETCH DISCOUNTS
  // =====================================================

  const fetchDiscounts =
    useCallback(
      async () => {
        setLoading(true);

        try {
          const response =
            await API.get(
              "/spacialdiscount",
              {
                params: {
                  page,
                  limit,

                  selectedcarcategory:
                    filterCatId || 0,

                  selectedcarmodel:
                    filterCarModelId || 0,

                  selectedseason:
                    filterSeasonId || 0,
                },
              }
            );

          const data =
            response.data || {};

          const discountData =
            Array.isArray(
              data.data
            )
              ? data.data
              : [];

          // -------------------------------------------------
          // IMPORTANT:
          // Ensure all names exist before displaying table.
          // -------------------------------------------------

          await ensureDiscountLookupData(
            discountData
          );

          setDiscounts(
            discountData
          );

          setTotal(
            Number(
              data.total || 0
            )
          );

          setTotalPages(
            Number(
              data.totalPages || 0
            )
          );
        } catch (error) {
          console.error(
            "fetchDiscounts error:",
            error
          );

          toasts.error(
            error.response?.data?.message ||
              error.response?.data?.error ||
              "Failed to load special discounts"
          );

          setDiscounts([]);
          setTotal(0);
          setTotalPages(0);
        } finally {
          setLoading(false);
        }
      },
      [
        page,
        limit,
        filterCatId,
        filterCarModelId,
        filterSeasonId,
        ensureDiscountLookupData,
      ]
    );

  // =====================================================
  // FETCH DISCOUNTS EFFECT
  // =====================================================

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  // =====================================================
  // GET CAR NAME
  // =====================================================

  const getCarName = (
    carmodelId
  ) => {
    if (!carmodelId) {
      return "—";
    }

    const car =
      allCars.find(
        (item) =>
          String(
            item.carmodel_id
          ) ===
          String(
            carmodelId
          )
      );

    return (
      car?.carmodel_name ||
      "—"
    );
  };

  // =====================================================
  // GET GROUP NAME
  // =====================================================

  const getGroupName = (
    catId
  ) => {
    if (!catId) {
      return "—";
    }

    const group =
      groups.find(
        (item) =>
          String(
            item.cat_id
          ) ===
          String(catId)
      );

    return (
      group?.cat_name ||
      "—"
    );
  };

  // =====================================================
  // GET SEASON NAME
  // =====================================================

  const getSeasonName = (
    seasonId
  ) => {
    if (!seasonId) {
      return "—";
    }

    const season =
      seasons.find(
        (item) =>
          String(
            item.season_id
          ) ===
          String(
            seasonId
          )
      );

    return (
      season?.season_name ||
      "—"
    );
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
    setFormCars([]);
  };

  // =====================================================
  // OPEN ADD MODAL
  // =====================================================

  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // =====================================================
  // CLOSE FORM MODAL
  // =====================================================

  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // =====================================================
  // GROUP CHANGE
  // =====================================================

  const handleGroupChange =
    async (catId) => {
      setForm(
        (previous) => ({
          ...previous,
          cat_id: catId,
          carmodel_id: "",
        })
      );

      setFormCars([]);

      if (!catId) {
        return;
      }

      await loadFormCars(
        catId
      );
    };

  // =====================================================
  // EDIT
  // =====================================================

  const startEdit =
    async (discount) => {
      try {
        setFormModal(true);

        const catId =
          discount.cat_id;

        setForm({
          cat_id: String(
            discount.cat_id ??
              ""
          ),

          carmodel_id: String(
            discount.carmodel_id ??
              ""
          ),

          season_id: String(
            discount.season_id ??
              ""
          ),

          spldisc_mindays:
            discount.spldisc_mindays ??
            "",

          discount:
            discount.discount ??
            "",
        });

        setEditId(
          discount.spldisc_id
        );

        if (catId) {
          await loadFormCars(
            catId
          );
        }
      } catch (error) {
        console.error(
          "startEdit error:",
          error
        );

        setFormModal(false);

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load discount"
        );
      }
    };

  // =====================================================
  // VIEW
  // =====================================================

  const handleView = (
    discount
  ) => {
    setViewModal(
      discount
    );
  };

  // =====================================================
  // FORM CHANGE
  // =====================================================

  const handleChange = (
    field,
    value
  ) => {
    setForm(
      (previous) => ({
        ...previous,
        [field]: value,
      })
    );
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit =
    async () => {
      if (
        !form.cat_id ||
        !form.carmodel_id ||
        !form.season_id ||
        !form.spldisc_mindays ||
        form.discount === ""
      ) {
        toasts.error(
          "Car Group, Car Model, Season, Minimum Booking Days and Discount are required"
        );

        return;
      }

      if (
        Number(
          form.spldisc_mindays
        ) <= 0
      ) {
        toasts.error(
          "Minimum booking days must be greater than 0"
        );

        return;
      }

      if (
        !Number.isInteger(
          Number(
            form.spldisc_mindays
          )
        )
      ) {
        toasts.error(
          "Minimum booking days must be a whole number"
        );

        return;
      }

      if (
        Number(
          form.discount
        ) < 0
      ) {
        toasts.error(
          "Discount cannot be negative"
        );

        return;
      }

      if (
        Number(
          form.discount
        ) > 100
      ) {
        toasts.error(
          "Discount cannot be greater than 100"
        );

        return;
      }

      setSaving(true);

      try {
        const payload = {
          cat_id: Number(
            form.cat_id
          ),

          carmodel_id:
            Number(
              form.carmodel_id
            ),

          season_id:
            Number(
              form.season_id
            ),

          spldisc_mindays:
            Number(
              form.spldisc_mindays
            ),

          discount:
            Number(
              form.discount
            ),
        };

        // =================================================
        // INSERT / UPDATE
        // =================================================

        if (editId) {
          await API.put(
            `/spacialdiscount/${editId}`,
            payload
          );

          toasts.success(
            "Special discount updated successfully"
          );
        } else {
          await API.post(
            "/spacialdiscount",
            payload
          );

          toasts.success(
            "Special discount added successfully"
          );
        }

        // =================================================
        // REFRESH EVERYTHING REQUIRED FOR TABLE
        // =================================================

        // Refresh group list
        const groupsResponse =
          await API.get(
            "/spacialdiscount/cat"
          );

        const freshGroups =
          getArrayData(
            groupsResponse
          );

        setGroups(
          (previous) =>
            mergeUniqueById(
              previous,
              freshGroups,
              "cat_id"
            )
        );

        // Refresh season list
        const seasonsResponse =
          await API.get(
            "/spacialdiscount/seasons"
          );

        const freshSeasons =
          getArrayData(
            seasonsResponse
          );

        setSeasons(
          (previous) =>
            mergeUniqueById(
              previous,
              freshSeasons,
              "season_id"
            )
        );

        // Refresh models for selected group
        if (form.cat_id) {
          const modelsResponse =
            await API.get(
              "/spacialdiscount/selectmodels",
              {
                params: {
                  cat_id:
                    form.cat_id,
                },
              }
            );

          const freshModels =
            getArrayData(
              modelsResponse
            );

          setFormCars(
            freshModels
          );

          setAllCars(
            (previous) =>
              mergeUniqueById(
                previous,
                freshModels,
                "carmodel_id"
              )
          );
        }

        // =================================================
        // CLOSE FORM
        // =================================================

        setFormModal(false);

        resetForm();

        // =================================================
        // FETCH TABLE AGAIN
        // =================================================
        await  fetchMeta();
        await fetchDiscounts();
      } catch (error) {
        console.error(
          "Submit error:",
          error
        );

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Operation failed"
        );
      } finally {
        setSaving(false);
      }
    };

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete =
    async () => {
      if (!delModal) {
        return;
      }

      setDelSaving(true);

      try {
        await API.delete(
          `/spacialdiscount/${delModal.spldisc_id}`
        );

        toasts.success(
          "Special discount deleted successfully"
        );

        setDelModal(null);

        if (
          discounts.length ===
            1 &&
          page > 1
        ) {
          setPage(
            (previous) =>
              previous - 1
          );
        } else {
          await fetchDiscounts();
          await  fetchMeta();
        }
      } catch (error) {
        console.error(
          "Delete error:",
          error
        );

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Delete failed"
        );
        
      } finally {
        setDelSaving(false);
      }
    };

  // =====================================================
  // RESET FILTERS
  // =====================================================

  const resetFilters = () => {
    setFilterCatId("");
    setFilterCarModelId("");
    setFilterSeasonId("");
    setPage(1);
  };
 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };
  // =====================================================
  // RETURN
  // =====================================================

  return (
    <Layout activePage="Special Discount">

      <main className="dashboard-main">

        <div className="table-card">

          {/* =================================================
              FILTER HEADER
          ================================================= */}

          <div className="table-card-head">

            {/* CAR GROUP */}

            <select
              value={filterCatId}
              onChange={(e) => {
                setFilterCatId(
                  e.target.value
                );
              }}
              className="season-input"
            >
              <option value="">
                All Car Groups
              </option>

              {Array.isArray(
                groups
              ) &&
                groups.map(
                  (group) => (
                    <option
                      key={
                        group.cat_id
                      }
                      value={
                        group.cat_id
                      }
                    >
                      {
                        group.cat_name
                      }
                    </option>
                  )
                )}
            </select>

            {/* CAR MODEL */}

            <select
              value={
                filterCarModelId
              }
              onChange={(e) => {
                setFilterCarModelId(
                  e.target.value
                );

                setPage(1);
              }}
              className="season-input"
              disabled={
                filterCarsLoading
              }
            >
              <option value="">
                {filterCarsLoading
                  ? "Loading..."
                  : "All Car Models"}
              </option>

              {Array.isArray(
                filterCars
              ) &&
                filterCars.map(
                  (car) => (
                    <option
                      key={
                        car.carmodel_id
                      }
                      value={
                        car.carmodel_id
                      }
                    >
                      {
                        car.carmodel_name
                      }
                    </option>
                  )
                )}
            </select>

            {/* SEASON */}

            <select
              value={
                filterSeasonId
              }
              onChange={(e) => {
                setFilterSeasonId(
                  e.target.value
                );

                setPage(1);
              }}
              className="season-input"
            >
              <option value="">
                All Seasons
              </option>

              {Array.isArray(
                seasons
              ) &&
                seasons.map(
                  (season) => (
                    <option
                      key={
                        season.season_id
                      }
                      value={
                        season.season_id
                      }
                    >
                      {
                        season.season_name
                      }
                    </option>
                  )
                )}
            </select>

            {/* RESET */}

            {(filterCatId ||
              filterCarModelId ||
              filterSeasonId) && (
              <button
                type="button"
                className="um-btn-secondary"
                onClick={
                  resetFilters
                }
              >
                Reset
              </button>
            )}

            {/* ADD */}

            <button
              type="button"
              className="um-btn-primary"
              onClick={
                openAddModal
              }
            >
              <span className="um-add-icon">
                ＋
              </span>

              Add Special Discount
            </button>

          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">

            <table className="um-table">

              <thead>

                <tr className="um-table-head">

                  <th className="um-th text-center">
                    #
                  </th>

                  <th className="um-th text-center">
                    Car Group
                  </th>

                  <th className="um-th text-center">
                    Car Model
                  </th>

                  <th className="um-th text-center">
                    Season
                  </th>

                  <th className="um-th text-center">
                    Min Booking Days
                  </th>

                  <th className="um-th text-center">
                    Discount
                  </th>

                  <th className="um-th text-center">
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {loading ? (

                  Array.from({
                    length: 5,
                  }).map(
                    (_, index) => (

                      <tr key={index}>

                        {Array.from({
                          length: 7,
                        }).map(
                          (
                            _,
                            cellIndex
                          ) => (

                            <td
                              key={
                                cellIndex
                              }
                              className="um-td text-center"
                            >

                              <div className="skeleton" />

                            </td>

                          )
                        )}

                      </tr>

                    )
                  )

                ) : discounts.length ===
                  0 ? (

                  <tr>

                    <td
                      colSpan={7}
                      className="empty-table-cell text-center py-5"
                    >

                      <div className="empty-icon">
                        <FiPercent />
                      </div>

                      <div className="empty-title">
                        No special discounts found
                      </div>

                      <div className="empty-sub">
                        Use the Add Special Discount
                        button to add one
                      </div>

                    </td>

                  </tr>

                ) : (

                  discounts.map(
                    (
                      discount,
                      index
                    ) => (

                      <tr
                        key={
                          discount.spldisc_id
                        }
                        className={`um-row ${
                          index % 2 === 0
                            ? "even-row"
                            : "odd-row"
                        }`}
                      >

                        <td className="um-td text-center">
                          {(page - 1) *
                            limit +
                            index +
                            1}
                        </td>

                        <td className="um-td text-center">
                          {getGroupName(
                            discount.cat_id
                          )}
                        </td>

                        <td className="um-td text-center">
                          {getCarName(
                            discount.carmodel_id
                          )}
                        </td>

                        <td className="um-td text-center">
                          {getSeasonName(
                            discount.season_id
                          )}
                        </td>

                        <td className="um-td text-center">

                          <span className="badge badge-success">
                            {
                              discount.spldisc_mindays
                            }{" "}
                            Days
                          </span>

                        </td>

                        <td className="um-td text-center">

                          <span className="badge badge-success">
                            {Number(
                              discount.discount
                            ).toFixed(2)}
                            %
                          </span>

                        </td>

                        <td className="um-td text-center">

                          <div className="action-buttons">

                            {/* VIEW */}

                            <button
                              type="button"
                              className="action-btn action-view"
                              title="View"
                              onClick={() =>
                                handleView(
                                  discount
                                )
                              }
                            >
                              <FiEye
                                size={15}
                              />
                            </button>

                            {/* EDIT */}

                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Edit"
                              onClick={() =>
                                startEdit(
                                  discount
                                )
                              }
                            >
                              <FiEdit
                                size={15}
                              />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="action-btn action-delete delete-enabled"
                              title="Delete"
                              onClick={() =>
                                setDelModal(
                                  discount
                                )
                              }
                            >
                              <FiTrash2
                                size={15}
                              />
                            </button>

                          </div>

                        </td>

                      </tr>

                    )
                  )

                )}

              </tbody>

            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>

        </div>

        {/* =====================================================
            ADD / EDIT MODAL
        ===================================================== */}

        {formModal && (

          <div
            className="um-overlay"
            onClick={
              closeFormModal
            }
          >

            <div
              className="um-modal um-view-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="um-modal-head">

                <div className="d-flex align-items-center gap-2">

                  <div className="form-card-icon">

                    {editId ? (
                      <FiEdit
                        size={24}
                      />
                    ) : (
                      <FiPlus
                        size={24}
                      />
                    )}

                  </div>

                  <div>

                    <h2 className="um-modal-title mb-0">

                      {editId
                        ? "Edit Special Discount"
                        : "Add Special Discount"}

                    </h2>

                    <p className="form-card-sub mb-0">

                      Configure discount based on
                      car, season and minimum
                      booking days

                    </p>

                  </div>

                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={
                    closeFormModal
                  }
                  disabled={
                    saving
                  }
                >
                  ✕
                </button>

              </div>

              <div className="um-modal-scroll">

                <div className="row g-3">

                  {/* CAR GROUP */}

                  <div className="col-md-6">

                    <label className="field-label">
                      Car Group *
                    </label>

                    <select
                      className="rd-input"
                      value={
                        form.cat_id
                      }
                      disabled={
                        saving
                      }
                      onChange={(e) =>
                        handleGroupChange(
                          e.target.value
                        )
                      }
                    >

                      <option value="">
                        — Select —
                      </option>

                      {Array.isArray(
                        carGroups
                      ) &&
                        carGroups.map(
                          (group) => (

                            <option
                              key={
                                group.cat_id
                              }
                              value={
                                group.cat_id
                              }
                            >
                              {
                                group.cat_name
                              }
                            </option>

                          )
                        )}

                    </select>

                  </div>

                  {/* CAR MODEL */}

                  <div className="col-md-6">

                    <label className="field-label">
                      Car Model *
                    </label>

                    <select
                      className="rd-input"
                      value={
                        form.carmodel_id
                      }
                      disabled={
                        !form.cat_id ||
                        formCarsLoading ||
                        saving
                      }
                      onChange={(e) =>
                        handleChange(
                          "carmodel_id",
                          e.target.value
                        )
                      }
                    >

                      <option value="">
                        {formCarsLoading
                          ? "Loading..."
                          : "— Select —"}
                      </option>

                      {Array.isArray(
                        formCars
                      ) &&
                        formCars.map(
                          (car) => (

                            <option
                              key={
                                car.carmodel_id
                              }
                              value={
                                car.carmodel_id
                              }
                            >
                              {
                                car.carmodel_name
                              }
                            </option>

                          )
                        )}

                    </select>

                  </div>

                  {/* SEASON */}

                  <div className="col-md-6">

                    <label className="field-label">
                      Season *
                    </label>

                    <select
                      className="rd-input"
                      value={
                        form.season_id
                      }
                      disabled={
                        saving
                      }
                      onChange={(e) =>
                        handleChange(
                          "season_id",
                          e.target.value
                        )
                      }
                    >

                      <option value="">
                        — Select —
                      </option>

                      {Array.isArray(
                        durationSeasons
                      ) &&
                        durationSeasons.map(
                          (season) => (

                            <option
                              key={
                                season.season_id
                              }
                              value={
                                season.season_id
                              }
                            >
                              {
                                season.season_name
                              }
                            </option>

                          )
                        )}

                    </select>

                  </div>

                  {/* MIN BOOKING DAYS */}

                  <div className="col-md-3">

                    <label className="field-label">
                      Min Booking Days *
                    </label>

                    <input
                      className="rd-input"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="e.g. 5"
                      value={
                        form.spldisc_mindays
                      }
                      disabled={
                        saving
                      }
                      onChange={(e) =>
                        handleChange(
                          "spldisc_mindays",
                          e.target.value
                        )
                      }
                    />

                  </div>

                  {/* DISCOUNT */}

                  <div className="col-md-3">

                    <label className="field-label">
                      Discount (%) *
                    </label>

                    <div className="position-relative">

                      <input
                        className="rd-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="e.g. 10"
                        value={
                          form.discount
                        }
                        disabled={
                          saving
                        }
                        onChange={(e) =>
                          handleChange(
                            "discount",
                            e.target.value
                          )
                        }
                        style={{
                          paddingRight: 35,
                        }}
                      />

                      <span
                        style={{
                          position:
                            "absolute",
                          right: 14,
                          top: "50%",
                          transform:
                            "translateY(-50%)",
                          fontWeight: 800,
                        }}
                      >
                        %
                      </span>

                    </div>

                  </div>

                </div>

              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={
                    closeFormModal
                  }
                  disabled={
                    saving
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={
                    handleSubmit
                  }
                  disabled={
                    saving
                  }
                >

                  {saving ? (

                    <span className="spinner" />

                  ) : editId ? (

                    <>
                      <FiSave />
                      Update Discount
                    </>

                  ) : (

                    <>
                      <FiPlus />
                      Add Discount
                    </>

                  )}

                </button>

              </div>

            </div>
          </div>
        )}

        {/* =====================================================
            VIEW MODAL
        ===================================================== */}

        {viewModal && (

          <div
            className="um-overlay"
            onClick={() =>
              setViewModal(null)
            }
          >

            <div
              className="um-modal um-view-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="um-modal-head">

                <div>

                  <h2 className="um-modal-title mb-0">

                    <FiPercent />{" "}
                    Special Discount Details

                  </h2>

                  <p className="form-card-sub mb-0">
                    Car and discount configuration
                  </p>

                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() =>
                    setViewModal(null)
                  }
                >
                  ✕
                </button>

              </div>

              <div className="um-modal-scroll">

                <div className="row g-3">

                  <div className="col-md-6">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Car Group
                      </div>

                      <div className="fw-bold">
                        {getGroupName(
                          viewModal.cat_id
                        )}
                      </div>

                    </div>

                  </div>

                  <div className="col-md-6">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Car Model
                      </div>

                      <div className="fw-bold">
                        {getCarName(
                          viewModal.carmodel_id
                        )}
                      </div>

                    </div>

                  </div>

                  <div className="col-md-6">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Season
                      </div>

                      <div className="fw-bold">
                        {getSeasonName(
                          viewModal.season_id
                        )}
                      </div>

                    </div>

                  </div>

                  <div className="col-md-3">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Minimum Days
                      </div>

                      <div className="fw-bold">
                        {
                          viewModal.spldisc_mindays
                        }{" "}
                        Days
                      </div>

                    </div>

                  </div>

                  <div className="col-md-3">

                    <div className="bg-light rounded-3 border p-3">

                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Discount
                      </div>

                      <div className="fw-bold text-success">
                        {
                          viewModal.discount
                        }%
                      </div>

                    </div>

                  </div>

                </div>

              </div>

              <div className="um-modal-foot">

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() =>
                    setViewModal(null)
                  }
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const data =
                      viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(data);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Discount
                </button>

              </div>

            </div>
          </div>
        )}

        {/* =====================================================
            DELETE MODAL
        ===================================================== */}

        {delModal && (

          <div
            className="um-overlay"
            onClick={() =>
              setDelModal(null)
            }
          >

            <div
              className="um-modal um-delete-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="delete-content">

                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">
                  Delete Special Discount?
                </h2>

                <div className="delete-badges">

                  <span className="badge badge-success">
                    {getCarName(
                      delModal.carmodel_id
                    )}
                  </span>

                  &nbsp;

                  <span className="badge badge-success">
                    {getSeasonName(
                      delModal.season_id
                    )}
                  </span>

                  &nbsp;

                  <span className="badge bg-warning text-dark">
                    {delModal.discount}%
                  </span>

                </div>

                <p className="delete-description">

                  This special discount
                  configuration will be
                  permanently deleted.

                  <br />

                  <strong>
                    This action cannot be undone.
                  </strong>

                </p>

                <div className="delete-actions">

                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() =>
                      setDelModal(null)
                    }
                    disabled={
                      delSaving
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={
                      delSaving
                    }
                    className="delete-confirm-btn"
                    onClick={
                      handleDelete
                    }
                  >

                    {delSaving ? (

                      <span className="spinner" />

                    ) : (

                      <>
                        <FiTrash2
                          size={16}
                        />
                        Delete Permanently
                      </>

                    )}

                  </button>

                </div>

              </div>

            </div>
          </div>
        )}

     
      </main>
    </Layout>
  );
}
